<template>
  <a-select v-model:value="valueState" :placeholder="placeholder" mode="multiple" allow-clear show-search
    option-filter-prop="label" option-label-prop="label" @search="onSearch" @select="() => (findText = null)"
    :disabled="disabled" :show-arrow="true" style="width: 100%">
    <template #notFoundContent>
      <template v-if="fetching">
        <a-spin size="small" /> Sedang mencari ...
      </template>
      <template v-else>
        Tidak ditemukan / Ketikkan Nama/ID Prov/Kota/Kab/Kec/Desa
      </template>
    </template>

    <a-select-option v-for="d in results" :key="d.id" :value="d.id" :label="d[label]">
      <span v-html="highlight(d[label])"></span>
    </a-select-option>
  </a-select>
</template>

<script>
import { ref, toRefs, watch, onMounted } from 'vue'
import apiClient from '@/services/axios'
import { useVModel } from '@/components/useVModel.js'
import { debounce, merge, keyBy, values } from 'lodash'

export default {
  props: {
    value: {
      type: Array,
      default: () => [], // multi select wajib array
    },
    disabled: Boolean,
    label: {
      type: String,
      default: 'name',
    },
    joinOptions: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Pilih Kabupaten/Kota',
    },
    level: {
      type: Number,
      default: null,
    },
    includeAll: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:value'],

  setup(props) {
    const results = ref([])
    const findText = ref(null)
    const fetching = ref(false)

    const valueState = useVModel(props, 'value')

    const { joinOptions, label } = toRefs(props)

    let controller

    // ================= FETCH =================
    const fetchData = async (q = '') => {
      // if (!q) return
      if (!props.level) return

      if (controller) controller.abort()

      controller = new AbortController()
      const { signal } = controller

      try {
        let { data } = await apiClient.get(`/api/filter/wilayah/${props.level}`, {
          signal,
          params: { q },
        })

        if (!data) return []

        // merge joinOptions
        const options = joinOptions.value.filter(o => o.id !== undefined)

        if (options.length > 0) {
          const merged = merge(keyBy(options, 'id'), keyBy(data, 'id'))
          data = values(merged)
        }

        const allOption = {
          id: 0,
          name: 'All Area',
        }

        const isSearching = q && q.trim() !== ''

        let mergedData = data.filter(d => d.id !== 0)

        if (props.includeAll && isSearching && data.length) {
          mergedData = [allOption, ...mergedData]
        }

        results.value = mergedData.sort((a, b) => {
          if (a.id === 0) return -1 // All Area selalu di atas
          if (b.id === 0) return 1
          return a[label.value].toLowerCase() > b[label.value].toLowerCase() ? 1 : -1
        })

        // results.value = data.sort((a, b) =>
        //   a[label.value].toLowerCase() > b[label.value].toLowerCase() ? 1 : -1,
        // )

        return data
      } catch (err) {
        if (err !== 'aborted') {
          console.error(err)
        }
      }
    }

    // ================= SEARCH =================
    const onSearch = debounce((val) => {
      fetching.value = true
      findText.value = val

      fetchData(val)
        .finally(() => {
          fetching.value = false
        })
    }, 300)

    // ================= HIGHLIGHT =================
    const highlight = (val) => {
      if (!val || !findText.value) return val

      try {
        return val.replace(new RegExp(findText.value, 'gi'), match => {
          return `<span style="background-color: yellow;">${match}</span>`
        })
      } catch {
        return val
      }
    }

    // ================= INIT =================
    onMounted(() => {
      if (props.level) {
        fetching.value = true

        fetchData('')
          .finally(() => {
            fetching.value = false
          })
      }

      if (joinOptions.value.length > 0) {
        results.value = [...joinOptions.value].sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
        )
      }
    })

    // ================= LOAD VALUE (EDIT MODE) =================
    watch(
      () => props.value,
      async (val) => {
        if (!val || val.length === 0) return

        // ambil data yang belum ada di results
        const missing = val.filter(id => !results.value.find(r => r.id === id))

        for (const id of missing) {
          try {
            const { data } = await apiClient.get(`/api/wilayah/${id}`)
            if (data) {
              results.value.push(data)
            }
          } catch (err) {
            console.error('failed fetch wilayah', err)
          }
        }
      },
      { immediate: true },
    )

    return {
      fetching,
      results,
      valueState,
      findText,
      highlight,
      onSearch,
    }
  },
}
</script>

<style scoped></style>